import React, { useState, useEffect, useMemo } from "react";
import LayerAdmin from "../LayerAdmin";
import { addnewOffers } from "../api/offers";
import { getGlobalFilter } from "../../../api/filters";
import { toast } from "react-toastify";
import ButtonLoader from "../../zamzam/components/ButtonLoader";
import { useNavigate } from "react-router-dom";
import URL_MAPPING from "../../../routes/constants";
import { delivery_man, preseller, salesman, sdr } from "../../../utility/constant";
import Dropdown from "../components/Dropdown";

const PERSONA_OPTIONS = [
  { value: salesman, label: "Salesman", name: "Salesman" },
  { value: preseller, label: "Preseller", name: "Preseller" },
  { value: "LMD", label: "LMD", name: "LMD" },
  { value: sdr, label: "SDR", name: "SDR" },
];

const AddNewOffer = () => {
  const [formData, setFormData] = useState<{
    upload_type: string;
    source: string;
    sales_office_id: string[];
    channel_id: string[];
    persona: string[];
  }>({
    upload_type: "",
    source: "",
    sales_office_id: [],
    channel_id: [],
    persona: [],
  });
  const [loading, setLoading] = useState(false);
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [invalidFields, setInvalidFields] = useState<string[]>([]);
  const [salesOffices, setSalesOffices] = useState<any[]>([]);
  const [channels, setChannels] = useState<any[]>([]);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchGlobalFilters = async () => {
      try {
        setLoadingFilters(true);
        const response = await getGlobalFilter({
          regions: ["SOUTH", "Riyadh Plastic", "CENTRAL", "EAST", "NORTH", "WEST"],
          positions: [salesman, preseller, sdr, delivery_man],
        });
        setSalesOffices(response?.sales_offices || []);
        setChannels(response?.channels || []);
      } catch (error) {
        console.error("Failed to fetch global filters:", error);
      } finally {
        setLoadingFilters(false);
      }
    };

    fetchGlobalFilters();
  }, []);

  const areBannerSfaFieldsEnabled =
    formData.upload_type === "banner" && formData.source === "BR";

  useEffect(() => {
    if (!areBannerSfaFieldsEnabled) {
      setFormData((prev) => ({
        ...prev,
        sales_office_id: [],
        channel_id: [],
        persona: [],
      }));
    }
  }, [areBannerSfaFieldsEnabled]);

  const salesOfficeOptions = useMemo(
    () =>
      salesOffices.map((office: any) => {
        const value = String(office.id ?? office.sales_office_id);
        const name = office.name ?? office.sales_office_name ?? value;
        return {
          value,
          name: `${name} (${value})`,
        };
      }),
    [salesOffices]
  );

  const channelOptions = useMemo(
    () =>
      channels.map((channel: any) => {
        const value = String(channel.id ?? channel.channel_id);
        const name = channel.name ?? channel.channel_name ?? value;
        return {
          value,
          name: `${name} (${value})`,
        };
      }),
    [channels]
  );

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleDropdownChange = (e: {
    target: { name: string; value: string | string[] };
  }) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: Array.isArray(value) ? value : value ? [value] : [],
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const getInputClass = (field: string) =>
    `w-full border px-3 py-2 rounded ${
      invalidFields.includes(field) ? "border-red-500" : ""
    }`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const form = new FormData();
    if (file) form.append("file", file, file.name);
    form.append("upload_type", formData.upload_type);
    form.append("source", formData.source);
    if (areBannerSfaFieldsEnabled) {
      form.append("sales_office_id", JSON.stringify(formData.sales_office_id));
      form.append("channel_id", JSON.stringify(formData.channel_id));
      form.append("persona", JSON.stringify(formData.persona));
    }

    try {
      setLoading(true);
      const bannerSfaFields = ["sales_office_id", "channel_id", "persona"];
      const emptyFields = Object.entries(formData)
        .filter(([key, value]) => {
          if (!areBannerSfaFieldsEnabled && bannerSfaFields.includes(key)) {
            return false;
          }
          if (Array.isArray(value)) {
            return value.length === 0;
          }
          return value === null || value === "";
        })
        .map(([key]) => key);
      if (file === null) {
        emptyFields.push("file");
      }

      setInvalidFields(emptyFields);

      if (emptyFields.length > 0) {
        toast.error("Please fill all required fields.");
        return;
      }
      const response = await addnewOffers(form);
      if (!response) {
        toast.error("No data received or malformed response");
        console.warn("No data received or malformed response:", response);
        return;
      }
      toast.success(response?.message);
      setTimeout(() => {
        navigate(URL_MAPPING.OFFERS);
      }, 2000);
    } catch (error: any) {
      console.error("Failed to fetch employee data:", error);
      if (error?.response?.status === 404) {
        toast.error("Route not found, Please try again later",{
          autoClose: false,
          closeOnClick: true,
          draggable: true,
        });
      } else if (error?.response?.status === 500) {
        toast.error("Error while fetching the employee",{
          autoClose: false,
          closeOnClick: true,
          draggable: true,
        });
      } else {
        const detail = error?.response?.data?.detail;
        if (Array.isArray(detail)) {
          detail.forEach((err) => toast.error(err.msg,{
            autoClose: false,
            closeOnClick: true,
            draggable: true,
          }));
        } else {
          toast.error(detail?.msg || detail || "An unexpected error occurred.",{
            autoClose: false,
            closeOnClick: true,
            draggable: true,
          });
        }
      }
    } finally {
      setLoading(false);
    }
  };
  return (
    <LayerAdmin>
      <div className="flex items-center px-5 pt-5 gap-3">
        <button
          onClick={() => window.history.back()}
          className="bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            xmlnsXlink="http://www.w3.org/1999/xlink"
            version="1.1"
            width="10"
            height="10"
            viewBox="0 0 256 256"
            xmlSpace="preserve"
          >
            <g
              style={{
                stroke: "none",
                strokeWidth: 0,
                strokeDasharray: "none",
                strokeLinecap: "butt",
                strokeLinejoin: "miter",
                strokeMiterlimit: 10,
                fill: "none",
                fillRule: "nonzero",
                opacity: 1,
              }}
              transform="translate(1.4065934065934016 1.4065934065934016) scale(2.81 2.81)"
            >
              <polygon
                points="20.48,45 59.75,90 69.52,81.48 37.69,45 69.52,8.52 59.75,0 "
                style={{
                  stroke: "none",
                  strokeWidth: 1,
                  strokeDasharray: "none",
                  strokeLinecap: "butt",
                  strokeLinejoin: "miter",
                  strokeMiterlimit: 10,
                  fill: "rgb(0,0,0)",
                  fillRule: "nonzero",
                  opacity: 1,
                }}
                transform="  matrix(1 0 0 1 0 0) "
              />
            </g>
          </svg>
        </button>
        <h3 className="text-xl font-bold text-gray-700">Add Offer</h3>
      </div>
      <form onSubmit={handleSubmit} className="p-4 lg:w-1/3 sm:w-full">
        <div className="my-2">
          <label className="block font-semibold mb-1">File *</label>
          <input
            type="file"
            name="file"
            accept=".jpg,.png"
            onChange={(e) => {
              const selectedFile = e.target.files?.[0];
              if (selectedFile) {
                const validTypes = ["image/jpeg", "image/png"];
                const maxSize = 5 * 1024 * 1024;

                if (!validTypes.includes(selectedFile.type)) {
                  toast.error(
                    "Invalid file type. Only JPG and PNG are allowed."
                  );
                  e.target.value = "";
                  return;
                }

                if (selectedFile.size > maxSize) {
                  toast.error("File size exceeds the maximum limit of 5MB.");
                  e.target.value = "";
                  return;
                }

                setFile(selectedFile);
              }
            }}
            className={getInputClass("file")}
          />
          <p className="text-sm text-gray-500 mt-1">
            Accepted file types: JPG, PNG. Maximum size: 5MB.
          </p>
        </div>

        <div className="my-2">
          <label className="block font-semibold mb-1">Upload Type *</label>
          <select
            name="upload_type"
            value={formData.upload_type}
            onChange={handleChange}
            className={getInputClass("upload_type")}
          >
            <option value="">Select type</option>
            <option value="banner">Banner</option>
            <option value="offer">Offer</option>
          </select>
        </div>

        <div className="my-2">
          <label className="block font-semibold mb-1">Source *</label>
          <select
            name="source"
            value={formData.source}
            onChange={handleChange}
            className={getInputClass("source")}
          >
            <option value="">Select source</option>
            <option value="B2B">B2B</option>
            <option value="BR">SFA</option>
          </select>
        </div>

        <div className="my-2">
          <label className="block font-semibold mb-1">
            Sales Office{areBannerSfaFieldsEnabled ? " *" : ""}
          </label>
          <Dropdown
            name="sales_office_id"
            value={formData.sales_office_id}
            onChange={handleDropdownChange}
            options={salesOfficeOptions}
            className={getInputClass("sales_office_id")}
            placeholder={
              loadingFilters
                ? "Loading sales offices..."
                : "Select sales offices"
            }
            searchable={true}
            multiple={true}
            disabled={loadingFilters || !areBannerSfaFieldsEnabled}
          />
        </div>

        <div className="my-2">
          <label className="block font-semibold mb-1">
            Channel{areBannerSfaFieldsEnabled ? " *" : ""}
          </label>
          <Dropdown
            name="channel_id"
            value={formData.channel_id}
            onChange={handleDropdownChange}
            options={channelOptions}
            className={getInputClass("channel_id")}
            placeholder={
              loadingFilters ? "Loading channels..." : "Select channels"
            }
            searchable={true}
            multiple={true}
            disabled={loadingFilters || !areBannerSfaFieldsEnabled}
          />
        </div>

        <div className="my-2">
          <label className="block font-semibold mb-1">
            Persona{areBannerSfaFieldsEnabled ? " *" : ""}
          </label>
          <Dropdown
            name="persona"
            value={formData.persona}
            onChange={handleDropdownChange}
            options={PERSONA_OPTIONS}
            className={getInputClass("persona")}
            placeholder="Select personas"
            searchable={false}
            multiple={true}
            disabled={!areBannerSfaFieldsEnabled}
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="bg-teal-500 text-white px-6 py-2 rounded hover:bg-teal-600 flex items-center gap-2"
        >
          Upload Offer {loading && <ButtonLoader />}
        </button>
      </form>
    </LayerAdmin>
  );
};

export default AddNewOffer;
